/**
 * Capital Expenditure Edit Form Component
 * सम्पादन गर्नुहोस् (Edit) - Same Form Structure as नयाँ डाटा थप्नुहोस्
 * 
 * This component provides the exact same table structure and functionality
 * as the main form, but for editing existing data
 */
import React, { useState, useEffect, useCallback } from 'react';
import styles from '../capitalExpenditure.module.scss';
import FirstQuarter from '../quarters/FirstQuarter';
import SecondQuarter from '../quarters/SecondQuarter';
import ThirdQuarter from '../quarters/ThirdQuarter';
import FourthQuarter from '../quarters/FourthQuarter';
import BasicInfoRow from '../components/BasicInfoRow';
import SummaryRow from '../components/SummaryRow';
import IncomeTotalRow, { emptyIncomeTotals, incomeStateFromApi } from '../components/IncomeTotalRow';
import InputField from '../components/InputField';
import { parseNumericValue, formatNumberForDisplay, calculateTotalWithFormatDetection, calculateCombinedTotalWithFormatDetection, calculateComplexTotalWithFormatDetection } from '../utils/numberUtils';
import { exportToExcel } from '../utils/excelExport';


const blankIfZero = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(num) && num === 0 ? '' : String(value);
};

const CapitalExpenditureEditForm = ({ 
  expenditureData, 
  onSave, 
  onCancel, 
  loading = false,
  viewMode = false,
  fiscalYears = [],
  onFiscalYearChange,
  yearSwitchLoading = false,
}) => {
  console.log('CapitalExpenditureEditForm rendered with props:', { expenditureData, onSave, onCancel, loading, viewMode });
  
  const [rows, setRows] = useState([]);
  const [operationalRows, setOperationalRows] = useState([]);
  const [incomeTotals, setIncomeTotals] = useState(emptyIncomeTotals());
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Handle click outside modal to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  // Show success message when data is saved
  const handleSaveSuccess = () => {
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000); // Hide after 3 seconds
  };


  const loadDataIntoForm = useCallback((data) => {
    console.log('loadDataIntoForm called with data:', data);
    console.log('financial_data in data:', data.financial_data);
    
    if (data.expenditure_items && data.expenditure_items.length > 0) {
      const capitalItems = [];
      const operationalItems = [];

      data.expenditure_items.forEach((item) => {
        const formItem = {
          id: item.id,
          codeNumber: item.code_number || '',
          expenseTitle: item.expense_title || '',
          unallocatedBudget: item.unallocated_budget || '',
          revisedBudget: item.revised_budget || '',
          q1BudgetDemand: item.q1_budget_demand || '',
          q1BudgetExpenditure: item.q1_budget_expenditure || '',
          q1AuthorityReceived: item.q1_authority_received || '',
          q1Expense: item.q1_expense || '',
          q1RemainingBalance: item.q1_remaining_balance || '0',
          q2BudgetDemand: item.q2_budget_demand || '',
          q2BudgetExpenditure: item.q2_budget_expenditure || '',
          q2AuthorityReceived: item.q2_authority_received || '',
          q2Expense: item.q2_expense || '',
          q2RemainingBalance: item.q2_remaining_balance || '0',
          q3BudgetDemand: item.q3_budget_demand || '',
          q3BudgetExpenditure: item.q3_budget_expenditure || '',
          q3AuthorityReceived: item.q3_authority_received || '',
          q3Expense: item.q3_expense || '',
          q3RemainingBalance: item.q3_remaining_balance || '0',
          q4BudgetDemand: item.q4_budget_demand || '',
          q4BudgetExpenditure: item.q4_budget_expenditure || '',
          q4AuthorityReceived: item.q4_authority_received || '',
          q4Expense: item.q4_expense || '',
          q4RemainingBalance: item.q4_remaining_balance || '0',
          totalBudgetDemand: blankIfZero(item.total_budget_demand),
          totalBudgetExpenditure: blankIfZero(item.total_budget_expenditure),
          totalAuthorityReceived: blankIfZero(item.total_authority_received),
          totalExpense: blankIfZero(item.total_expense),
          totalRemainingBalance: blankIfZero(item.total_remaining_balance),
        };

        if (item.item_type === 'capital') {
          capitalItems.push(formItem);
        } else if (item.item_type === 'operational') {
          operationalItems.push(formItem);
        }
      });

      setRows(capitalItems.length > 0 ? capitalItems : [getEmptyCapitalRow()]);
      setOperationalRows(operationalItems.length > 0 ? operationalItems : [getEmptyOperationalRow()]);
    } else {
      setRows([getEmptyCapitalRow()]);
      setOperationalRows([getEmptyOperationalRow()]);
    }

    setIncomeTotals(incomeStateFromApi(data));

  }, []);

  useEffect(() => {
    if (expenditureData) {
      loadDataIntoForm(expenditureData);
    }
  }, [expenditureData, loadDataIntoForm]);

  const getEmptyCapitalRow = () => ({
    id: Date.now(),
    codeNumber: '',
    expenseTitle: '',
    unallocatedBudget: '',
    revisedBudget: '',
    q1BudgetDemand: '',
    q1BudgetExpenditure: '',
    q1AuthorityReceived: '',
    q1Expense: '',
    q1RemainingBalance: '0',
    q2BudgetDemand: '',
    q2BudgetExpenditure: '',
    q2AuthorityReceived: '',
    q2Expense: '',
    q2RemainingBalance: '0',
    q3BudgetDemand: '',
    q3BudgetExpenditure: '',
    q3AuthorityReceived: '',
    q3Expense: '',
    q3RemainingBalance: '0',
    q4BudgetDemand: '',
    q4BudgetExpenditure: '',
    q4AuthorityReceived: '',
    q4Expense: '',
    q4RemainingBalance: '0',
    totalBudgetDemand: '',
    totalBudgetExpenditure: '',
    totalAuthorityReceived: '',
    totalExpense: '',
    totalRemainingBalance: '',
  });

  const getEmptyOperationalRow = () => ({
    id: `op_${Date.now()}`,
    codeNumber: '',
    expenseTitle: '',
    unallocatedBudget: '',
    revisedBudget: '',
    q1BudgetDemand: '',
    q1BudgetExpenditure: '',
    q1AuthorityReceived: '',
    q1Expense: '',
    q1RemainingBalance: '0',
    q2BudgetDemand: '',
    q2BudgetExpenditure: '',
    q2AuthorityReceived: '',
    q2Expense: '',
    q2RemainingBalance: '0',
    q3BudgetDemand: '',
    q3BudgetExpenditure: '',
    q3AuthorityReceived: '',
    q3Expense: '',
    q3RemainingBalance: '0',
    q4BudgetDemand: '',
    q4BudgetExpenditure: '',
    q4AuthorityReceived: '',
    q4Expense: '',
    q4RemainingBalance: '0',
    totalBudgetDemand: '',
    totalBudgetExpenditure: '',
    totalAuthorityReceived: '',
    totalExpense: '',
    totalRemainingBalance: '',
  });

  const handleInputChange = (rowId, field, value) => {
    setRows(prevRows => {
      const updatedRows = prevRows.map(row => 
        row.id === rowId ? { ...row, [field]: value } : row
      );
      
      // Auto-calculate remaining balance
      const quarterMatch = field.match(/^q(\d)(AuthorityReceived|Expense)$/);
      if (quarterMatch) {
        const quarter = quarterMatch[1];
        const authorityField = `q${quarter}AuthorityReceived`;
        const expenseField = `q${quarter}Expense`;
        const remainingField = `q${quarter}RemainingBalance`;
        
        const updatedRow = updatedRows.find(row => row.id === rowId);
        const authority = parseFloat(updatedRow[authorityField] || '0');
        const expense = parseFloat(updatedRow[expenseField] || '0');
        const remaining = Math.max(0, authority - expense);
        
        return updatedRows.map(row => 
          row.id === rowId ? { ...row, [remainingField]: remaining.toString() } : row
        );
      }
      return updatedRows;
    });
  };

  const handleOperationalRowChange = (rowId, field, value) => {
    setOperationalRows(prevRows => {
      const updatedRows = prevRows.map(row => 
        row.id === rowId ? { ...row, [field]: value } : row
      );
      
      // Auto-calculate remaining balance
      const quarterMatch = field.match(/^q(\d)(AuthorityReceived|Expense)$/);
      if (quarterMatch) {
        const quarter = quarterMatch[1];
        const authorityField = `q${quarter}AuthorityReceived`;
        const expenseField = `q${quarter}Expense`;
        const remainingField = `q${quarter}RemainingBalance`;
        
        const updatedRow = updatedRows.find(row => row.id === rowId);
        const authority = parseFloat(updatedRow[authorityField] || '0');
        const expense = parseFloat(updatedRow[expenseField] || '0');
        const remaining = Math.max(0, authority - expense);
        
        return updatedRows.map(row => 
          row.id === rowId ? { ...row, [remainingField]: remaining.toString() } : row
        );
      }
      return updatedRows;
    });
  };

  const addRow = () => {
    setRows(prevRows => [...prevRows, getEmptyCapitalRow()]);
  };

  const addOperationalRow = () => {
    setOperationalRows(prevRows => [...prevRows, getEmptyOperationalRow()]);
  };

  const removeRow = (rowId) => {
    if (rowId.toString().startsWith('op_')) {
      setOperationalRows(prevRows => prevRows.filter(row => row.id !== rowId));
    } else {
      setRows(prevRows => prevRows.filter(row => row.id !== rowId));
    }
  };

  const handleIncomeChange = (field, value) => {
    setIncomeTotals(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const formData = {
      id: expenditureData.id,
      rows: rows.filter(row => 
        row.codeNumber.trim() || 
        row.expenseTitle.trim() || 
        parseFloat(row.unallocatedBudget) > 0 || 
        parseFloat(row.revisedBudget) > 0
      ),
      operationalRows: operationalRows.filter(row => 
        row.codeNumber.trim() || 
        row.expenseTitle.trim() || 
        parseFloat(row.unallocatedBudget) > 0 || 
        parseFloat(row.revisedBudget) > 0
      ),
      incomeTotals,
    };

    onSave(formData);
    handleSaveSuccess(); // Show success message
  };

  // Function to handle Excel download
  const handleDownloadExcel = () => {
    try {
      // Prepare data in the format expected by exportToExcel
      const exportData = {
        institution: { name: expenditureData.institution_name },
        fiscal_year: { display: expenditureData.fiscal_year_display },
        expenditure_items: [
          ...rows.map(row => ({
            code_number: row.codeNumber,
            expense_title: row.expenseTitle,
            unallocated_budget: row.unallocatedBudget,
            revised_budget: row.revisedBudget,
            q1_budget_demand: row.q1BudgetDemand,
            q1_budget_expenditure: row.q1BudgetExpenditure,
            q1_authority_received: row.q1AuthorityReceived,
            q1_expense: row.q1Expense,
            q1_remaining_balance: row.q1RemainingBalance,
            q2_budget_demand: row.q2BudgetDemand,
            q2_budget_expenditure: row.q2BudgetExpenditure,
            q2_authority_received: row.q2AuthorityReceived,
            q2_expense: row.q2Expense,
            q2_remaining_balance: row.q2RemainingBalance,
            q3_budget_demand: row.q3BudgetDemand,
            q3_budget_expenditure: row.q3BudgetExpenditure,
            q3_authority_received: row.q3AuthorityReceived,
            q3_expense: row.q3Expense,
            q3_remaining_balance: row.q3RemainingBalance,
            q4_budget_demand: row.q4BudgetDemand,
            q4_budget_expenditure: row.q4BudgetExpenditure,
            q4_authority_received: row.q4AuthorityReceived,
            q4_expense: row.q4Expense,
            q4_remaining_balance: row.q4RemainingBalance,
            item_type: 'capital'
          })),
          ...operationalRows.map(row => ({
            code_number: row.codeNumber,
            expense_title: row.expenseTitle,
            unallocated_budget: row.unallocatedBudget,
            revised_budget: row.revisedBudget,
            q1_budget_demand: row.q1BudgetDemand,
            q1_budget_expenditure: row.q1BudgetExpenditure,
            q1_authority_received: row.q1AuthorityReceived,
            q1_expense: row.q1Expense,
            q1_remaining_balance: row.q1RemainingBalance,
            q2_budget_demand: row.q2BudgetDemand,
            q2_budget_expenditure: row.q2BudgetExpenditure,
            q2_authority_received: row.q2AuthorityReceived,
            q2_expense: row.q2Expense,
            q2_remaining_balance: row.q2RemainingBalance,
            q3_budget_demand: row.q3BudgetDemand,
            q3_budget_expenditure: row.q3BudgetExpenditure,
            q3_authority_received: row.q3AuthorityReceived,
            q3_expense: row.q3Expense,
            q3_remaining_balance: row.q3RemainingBalance,
            q4_budget_demand: row.q4BudgetDemand,
            q4_budget_expenditure: row.q4BudgetExpenditure,
            q4_authority_received: row.q4AuthorityReceived,
            q4_expense: row.q4Expense,
            q4_remaining_balance: row.q4RemainingBalance,
            item_type: 'operational'
          }))
        ]
      };

      // Generate filename with institution and fiscal year
      const filename = `${expenditureData.institution_name}_${expenditureData.fiscal_year_display}_टोकन_डाटा`;
      
      // Export to Excel
      const result = exportToExcel(exportData, filename);
      
      if (result.success) {
        // Show success message
        console.log('Excel file downloaded successfully:', result.filename);
      } else {
        console.error('Error downloading Excel:', result.error);
      }
    } catch (error) {
      console.error('Error preparing data for Excel export:', error);
    }
  };

  if (!expenditureData) {
    console.log('No expenditure data provided to edit form');
    return (
      <div className={styles.noData}>
        <h3>सम्पादन गर्न डाटा छैन</h3>
        <p>कृपया सम्पादन गर्न डाटा छान्नुहोस्</p>
        <p>Debug: expenditureData = {JSON.stringify(expenditureData)}</p>
      </div>
    );
  }

  return (
    <div className={styles.editFormContainer} onClick={handleBackdropClick}>
      <div className={styles.editFormContent}>
        <div className={styles.editFormHeader}>
          <div className={styles.headerContent}>
            <h2>सम्पादन फारम - {expenditureData.institution_name} ({expenditureData.fiscal_year_display})</h2>
            {onFiscalYearChange && fiscalYears.length > 0 && (
              <div className={styles.editFormYearSwitch}>
                <label htmlFor="token-form-fiscal-year">आर्थिक वर्ष</label>
                <select
                  id="token-form-fiscal-year"
                  value={expenditureData.fiscal_year || ''}
                  disabled={yearSwitchLoading || loading}
                  onChange={(e) => {
                    const nextId = parseInt(e.target.value, 10);
                    if (!nextId || nextId === Number(expenditureData.fiscal_year)) {
                      return;
                    }
                    if (!window.confirm('आर्थिक वर्ष बदल्दा सेभ नभएका परिवर्तन हराउन सक्छन्। जारी राख्ने हो?')) {
                      return;
                    }
                    onFiscalYearChange(nextId);
                  }}
                >
                  {fiscalYears.map((fiscalYear) => (
                    <option key={fiscalYear.id} value={fiscalYear.id}>
                      {fiscalYear.name}{fiscalYear.is_current ? ' (चालु)' : ''}
                    </option>
                  ))}
                </select>
                {yearSwitchLoading && <span className={styles.yearSwitchLoading}>लोड हुँदैछ...</span>}
              </div>
            )}
            {expenditureData.source_fiscal_year_display && (
              <p className={styles.currentYearFormNote}>
                {expenditureData.fiscal_year_display === expenditureData.source_fiscal_year_display
                  ? `यो टोकनको मूल आर्थिक वर्ष ${expenditureData.source_fiscal_year_display} हो।`
                  : `टोकन ${expenditureData.source_fiscal_year_display} को हो; पुरानो डाटा सुरक्षित छ।`}
              </p>
            )}
            {showSuccessMessage && (
              <div className={styles.successMessage}>
                ✅ डाटा सफलतापूर्वक अपडेट भयो!
              </div>
            )}
          </div>
          <div className={styles.editFormActions}>
            <button 
              type="button"
              onClick={handleDownloadExcel}
              className={styles.downloadExcelButton}
              title="एक्सेल फाइल डाउनलोड गर्नुहोस्"
            >
              📊 एक्सेल डाउनलोड
            </button>
            <button 
              type="submit" 
              onClick={handleSubmit}
              className={styles.saveButton}
              disabled={loading}
            >
              {loading ? 'सेभ गर्दै...' : 'सेभ गर्नुहोस्'}
            </button>
          </div>
          <button 
            type="button" 
            onClick={onCancel}
            className={styles.closeButton}
            title="बन्द गर्नुहोस्"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.editForm}>
        {/* Same Table Structure as नयाँ डाटा थप्नुहोस् (Add New Data) */}
        <div className={styles.tableContainer}>
          <table className={styles.expenditureTable}>
            <thead>
              <tr className={styles.mainHeader}>
                <th rowSpan="2" className={styles.codeHeader}>कोड नं.</th>
                <th rowSpan="2" className={styles.titleHeader}>खर्च शिर्षक</th>
                <th rowSpan="2" className={`${styles.budgetHeader} ${styles.budgetHeaderSticky3}`}>बिनियोजित बजेट</th>
                <th rowSpan="2" className={`${styles.budgetHeader} ${styles.budgetHeaderSticky4}`}>परिमार्जीत बजेट</th>
                <th colSpan="5">प्रथम त्रैमासिक</th>
                <th colSpan="5">दोस्रो त्रैमासिक</th>
                <th colSpan="5">तेस्रो त्रैमासिक</th>
                <th colSpan="5">चौथो त्रैमासिक</th>
                <th colSpan="5">यस आ.व.को कुल बजेट विवरण</th>
                <th colSpan="1">विनियोजित बजेट र अर्च विचको भिन्नता</th>
              </tr>
              <tr className={styles.subHeader}>
                <th>बजेट माग</th>
                <th>बजेट निकासा</th>
                <th>अख्तियारी प्राप्त</th>
                <th>खर्च</th>
                <th>बाँकि मौज्दात</th>
                <th>बजेट माग</th>
                <th>बजेट निकासा</th>
                <th>अख्तियारी प्राप्त</th>
                <th>खर्च</th>
                <th>बाँकि मौज्दात</th>
                <th>बजेट माग</th>
                <th>बजेट निकासा</th>
                <th>अख्तियारी प्राप्त</th>
                <th>खर्च</th>
                <th>बाँकि मौज्दात</th>
                <th>बजेट माग</th>
                <th>बजेट निकासा</th>
                <th>अख्तियारी प्राप्त</th>
                <th>खर्च</th>
                <th>बाँकि मौज्दात</th>
                <th>कुल बजेट माग</th>
                <th>कुल बजेट निकासा</th>
                <th>कुल अख्तियारी प्राप्त</th>
                <th>कुल खर्च</th>
                <th>जम्मा मौज्दात</th>
                <th>भिन्नता</th>
              </tr>
            </thead>
            <tbody>
              {/* Capital Expenditure Section */}
              <tr className={styles.categoryRow}>
                <td colSpan="4" className={`${styles.categoryCell} ${styles.stickyColGroup4}`}>पुँजीगत खर्च</td>
                <td colSpan="26" className={styles.categoryCell}></td>
              </tr>
              
              {rows.filter((row) => (row.codeNumber && row.expenseTitle) || row.isNewlyAdded).map((row) => {
                return (
                  <tr key={row.id} className={styles.dataRow}>
                    {/* Basic Info */}
                    <BasicInfoRow
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                    />
                    
                    {/* Quarter 1 */}
                    <FirstQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                    />
                    
                    {/* Quarter 2 */}
                    <SecondQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                    />
                    
                    {/* Quarter 3 */}
                    <ThirdQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                    />
                    
                    {/* Quarter 4 */}
                    <FourthQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                    />
                    
                    {/* Summary Columns */}
                    <SummaryRow
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                      budgetAllocationDifference={parseNumericValue(row.revisedBudget) - parseNumericValue(row.totalBudgetDemand)}
                    />
                    
                    {/* Action Buttons */}
                    {!viewMode && (
                      <td className={styles.actionCell}>
                        <button
                          type="button"
                          onClick={() => removeRow(row.id)}
                          className={styles.removeButton}
                        >
                          हटाउनुहोस्
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              
              {/* Capital Expenditure Total Row */}
              <tr className={styles.totalRow}>
                <td colSpan="2" className={`${styles.totalLabel} ${styles.stickyColGroup2}`}>जम्मा पुँजीगत</td>
                <td className={`${styles.budgetCell} ${styles.stickyCol3}`}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.unallocatedBudget), 0), true)}
                    onChange={(value) => handleInputChange(null, 'unallocatedBudget', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={`${styles.budgetCell} ${styles.stickyCol4}`}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.revisedBudget), 0), true)}
                    onChange={(value) => handleInputChange(null, 'revisedBudget', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetDemand), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q1BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetExpenditure), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q1BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q1AuthorityReceived), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q1AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q1Expense), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q1Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q1RemainingBalance), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q1RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetDemand), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q2BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetExpenditure), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q2BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q2AuthorityReceived), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q2AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q2Expense), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q2Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q2RemainingBalance), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q2RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetDemand), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q3BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetExpenditure), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q3BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q3AuthorityReceived), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q3AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q3Expense), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q3Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q3RemainingBalance), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q3RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetDemand), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q4BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetExpenditure), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q4BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q4AuthorityReceived), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q4AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q4Expense), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q4Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(rows.reduce((sum, row) => sum + parseNumericValue(row.q4RemainingBalance), 0), true)}
                    onChange={(value) => handleInputChange(null, 'q4RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateTotalWithFormatDetection(rows, 'totalBudgetDemand')}
                    onChange={(value) => handleInputChange(null, 'totalBudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateTotalWithFormatDetection(rows, 'totalBudgetExpenditure')}
                    onChange={(value) => handleInputChange(null, 'totalBudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateTotalWithFormatDetection(rows, 'totalAuthorityReceived')}
                    onChange={(value) => handleInputChange(null, 'totalAuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateTotalWithFormatDetection(rows, 'totalExpense')}
                    onChange={(value) => handleInputChange(null, 'totalExpense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateTotalWithFormatDetection(rows, 'totalRemainingBalance')}
                    onChange={(value) => handleInputChange(null, 'totalRemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateComplexTotalWithFormatDetection(rows, (row) =>
                      parseNumericValue(row.revisedBudget) - parseNumericValue(row.totalBudgetDemand)
                    )}
                    onChange={(value) => handleInputChange(null, 'budgetAllocationDifference', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
              </tr>
              
              {/* Add Capital Row Button */}
              {!viewMode && (
                <tr>
                  <td colSpan="30" className={styles.addRowCell}>
                    <button
                      type="button"
                      onClick={addRow}
                      className={styles.addRowButton}
                    >
                      पुँजीगत खर्च (+ पंक्ति थप्नुहोस्)
                    </button>
                  </td>
                </tr>
              )}
              
              {/* Operational Expenditure Section */}
              <tr className={styles.categoryRow}>
                <td colSpan="4" className={`${styles.categoryCell} ${styles.stickyColGroup4}`}>संचालन खर्च</td>
                <td colSpan="26" className={styles.categoryCell}></td>
              </tr>
              
              {operationalRows.filter((row) => (row.codeNumber && row.expenseTitle) || row.isNewlyAdded).map((row) => {
                return (
                  <tr key={row.id} className={styles.dataRow}>
                    {/* Basic Info */}
                    <BasicInfoRow
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                      handleOperationalRowChange={handleOperationalRowChange}
                      isOperational={true}
                    />
                    
                    {/* Quarter 1 */}
                    <FirstQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                      handleOperationalRowChange={handleOperationalRowChange}
                      isOperational={true}
                    />
                    
                    {/* Quarter 2 */}
                    <SecondQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                      handleOperationalRowChange={handleOperationalRowChange}
                      isOperational={true}
                    />
                    
                    {/* Quarter 3 */}
                    <ThirdQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                      handleOperationalRowChange={handleOperationalRowChange}
                      isOperational={true}
                    />
                    
                    {/* Quarter 4 */}
                    <FourthQuarter 
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                      handleOperationalRowChange={handleOperationalRowChange}
                      isOperational={true}
                    />
                    
                    {/* Summary Columns */}
                    <SummaryRow
                      row={row}
                      viewMode={viewMode}
                      handleInputChange={handleInputChange}
                      handleOperationalRowChange={handleOperationalRowChange}
                      isOperational={true}
                      budgetAllocationDifference={parseNumericValue(row.revisedBudget) - parseNumericValue(row.totalBudgetDemand)}
                    />
                    
                    {/* Action Buttons */}
                    {!viewMode && (
                      <td className={styles.actionCell}>
                        <button
                          type="button"
                          onClick={() => removeRow(row.id)}
                          className={styles.removeButton}
                        >
                          हटाउनुहोस्
                        </button>
                      </td>
                    )}
                  </tr>
                                 );
               })}
               
                                              {/* Operational Expenditure Total Row */}
               <tr className={styles.totalRow}>
                 <td colSpan="2" className={`${styles.totalLabel} ${styles.stickyColGroup2}`}>जम्मा संचालन</td>
                  <td className={`${styles.budgetCell} ${styles.stickyCol3}`}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.unallocatedBudget), 0), true)}
                      onChange={(value) => handleInputChange(null, 'unallocatedBudget', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={`${styles.budgetCell} ${styles.stickyCol4}`}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.revisedBudget), 0), true)}
                      onChange={(value) => handleInputChange(null, 'revisedBudget', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetDemand), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q1BudgetDemand', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetExpenditure), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q1BudgetExpenditure', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1AuthorityReceived), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q1AuthorityReceived', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1Expense), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q1Expense', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1RemainingBalance), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q1RemainingBalance', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetDemand), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q2BudgetDemand', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetExpenditure), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q2BudgetExpenditure', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2AuthorityReceived), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q2AuthorityReceived', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2Expense), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q2Expense', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2RemainingBalance), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q2RemainingBalance', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetDemand), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q3BudgetDemand', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetExpenditure), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q3BudgetExpenditure', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3AuthorityReceived), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q3AuthorityReceived', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3Expense), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q3Expense', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3RemainingBalance), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q3RemainingBalance', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetDemand), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q4BudgetDemand', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetExpenditure), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q4BudgetExpenditure', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4AuthorityReceived), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q4AuthorityReceived', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4Expense), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q4Expense', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={formatNumberForDisplay(operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4RemainingBalance), 0), true)}
                      onChange={(value) => handleInputChange(null, 'q4RemainingBalance', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={calculateTotalWithFormatDetection(operationalRows, 'totalBudgetDemand')}
                      onChange={(value) => handleInputChange(null, 'totalBudgetDemand', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={calculateTotalWithFormatDetection(operationalRows, 'totalBudgetExpenditure')}
                      onChange={(value) => handleInputChange(null, 'totalBudgetExpenditure', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={calculateTotalWithFormatDetection(operationalRows, 'totalAuthorityReceived')}
                      onChange={(value) => handleInputChange(null, 'totalAuthorityReceived', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={calculateTotalWithFormatDetection(operationalRows, 'totalExpense')}
                      onChange={(value) => handleInputChange(null, 'totalExpense', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={calculateTotalWithFormatDetection(operationalRows, 'totalRemainingBalance')}
                      onChange={(value) => handleInputChange(null, 'totalRemainingBalance', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                  <td className={styles.budgetCell}>
                    <InputField
                      value={calculateComplexTotalWithFormatDetection(operationalRows, (row) =>
                        parseNumericValue(row.revisedBudget) - parseNumericValue(row.totalBudgetDemand)
                      )}
                      onChange={(value) => handleInputChange(null, 'budgetAllocationDifference', value)}
                      disabled={false}
                      viewMode={viewMode}
                    />
                  </td>
                </tr>
                
                {/* Combined Total Row - Operational + Capital */}
                <tr className={styles.totalRow}>
                  <td colSpan="2" className={`${styles.totalLabel} ${styles.stickyColGroup2}`}>जम्मा संचालन र पुँजीगत खर्च</td>
                <td className={`${styles.budgetCell} ${styles.stickyCol3}`}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.unallocatedBudget), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.unallocatedBudget), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'unallocatedBudget', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={`${styles.budgetCell} ${styles.stickyCol4}`}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.revisedBudget), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.revisedBudget), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'revisedBudget', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetDemand), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetDemand), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q1BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetExpenditure), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q1BudgetExpenditure), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q1BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1AuthorityReceived), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q1AuthorityReceived), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q1AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1Expense), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q1Expense), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q1Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q1RemainingBalance), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q1RemainingBalance), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q1RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetDemand), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetDemand), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q2BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetExpenditure), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q2BudgetExpenditure), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q2BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2AuthorityReceived), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q2AuthorityReceived), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q2AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2Expense), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q2Expense), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q2Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q2RemainingBalance), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q2RemainingBalance), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q2RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetDemand), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetDemand), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q3BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetExpenditure), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q3BudgetExpenditure), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q3BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3AuthorityReceived), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q3AuthorityReceived), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q3AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3Expense), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q3Expense), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q3Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q3RemainingBalance), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q3RemainingBalance), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q3RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetDemand), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetDemand), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q4BudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetExpenditure), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q4BudgetExpenditure), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q4BudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4AuthorityReceived), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q4AuthorityReceived), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q4AuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4Expense), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q4Expense), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q4Expense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + parseNumericValue(row.q4RemainingBalance), 0)) +
                      (rows.reduce((sum, row) => sum + parseNumericValue(row.q4RemainingBalance), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'q4RemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateCombinedTotalWithFormatDetection(operationalRows, rows, 'totalBudgetDemand')}
                    onChange={(value) => handleInputChange(null, 'totalBudgetDemand', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateCombinedTotalWithFormatDetection(operationalRows, rows, 'totalBudgetExpenditure')}
                    onChange={(value) => handleInputChange(null, 'totalBudgetExpenditure', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateCombinedTotalWithFormatDetection(operationalRows, rows, 'totalAuthorityReceived')}
                    onChange={(value) => handleInputChange(null, 'totalAuthorityReceived', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateCombinedTotalWithFormatDetection(operationalRows, rows, 'totalExpense')}
                    onChange={(value) => handleInputChange(null, 'totalExpense', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={calculateCombinedTotalWithFormatDetection(operationalRows, rows, 'totalRemainingBalance')}
                    onChange={(value) => handleInputChange(null, 'totalRemainingBalance', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
                <td className={styles.budgetCell}>
                  <InputField
                    value={formatNumberForDisplay(
                      (operationalRows.reduce((sum, row) => sum + (parseNumericValue(row.revisedBudget) - parseNumericValue(row.totalBudgetDemand)), 0)) +
                      (rows.reduce((sum, row) => sum + (parseNumericValue(row.revisedBudget) - parseNumericValue(row.totalBudgetDemand)), 0)), true
                    )}
                    onChange={(value) => handleInputChange(null, 'budgetAllocationDifference', value)}
                    disabled={false}
                    viewMode={viewMode}
                  />
                </td>
               </tr>

                <IncomeTotalRow
                  incomeTotals={incomeTotals}
                  onIncomeChange={handleIncomeChange}
                  viewMode={viewMode}
                />
               
                                                               {/* Add Operational Row Button */}
                 {!viewMode && (
                   <tr>
                     <td colSpan="30" className={styles.addRowCell}>
                       <button
                         type="button"
                         onClick={addOperationalRow}
                         className={styles.addRowButton}
                       >
                         संचालन खर्च (+ पंक्ति थप्नुहोस्)
                       </button>
                     </td>
                   </tr>
                 )}
               

            </tbody>
          </table>
        </div>
      </form>
        </div>
      </div>
    );
  };

export default CapitalExpenditureEditForm;
